import { BONUS_SYMBOL_ID, MYSTERY_SYMBOL_ID } from "../../SymbolData";

/**
 * Staging for the Mystery (B1) reveal.
 *
 * ── The problem this solves ──
 *
 * The backend resolves the mystery before it answers: `outcome.screen` already
 * holds the symbol the mysteries became, and `outcome.storage.mystery_feature`
 * separately reports where they dropped. So the board we are handed is the
 * board AFTER the reveal, and the pre-reveal state — the one the player must
 * actually watch land — exists nowhere but here.
 *
 * ── Why the mask is display-only ──
 *
 * `currentRound.reels` in Redux must keep the RESOLVED board. useWinController
 * reads it to decide which spine celebrates, the win boxes read positions off
 * it, and the paytable lookups follow the same ids. Put a 9 in there and the
 * win presentation animates the mystery symbol as the winner.
 *
 * So only the array handed to a reel CONTROLLER is masked. The round is never
 * rewritten, and after the reveal the controller is told to become the real
 * symbol — see replaceSymbolAtPosition, which rewrites identity as well as
 * texture because the cell must genuinely BE that symbol before wins are read.
 */

/**
 * @param {object} round A round with `storage.mystery_feature`.
 * @returns {{coords: Array<[number, number]>, replacementId: number}|null}
 */
export const readMysteryFeature = (round) => {
  const feature = round?.storage?.mystery_feature;
  const coords = feature?.coords;
  if (!Array.isArray(coords) || coords.length === 0) return null;

  // The API sends symbol indexes as STRINGS ("10"), per the BE doc. Everything
  // downstream compares numbers.
  const replacementId = Number(feature.replacement);
  if (!Number.isFinite(replacementId)) return null;

  return { coords, replacementId };
};

/**
 * The rows on one reel that landed as mystery.
 *
 * @param {object} round
 * @param {number} reelIndex
 * @returns {number[]} rows, empty when this reel has none
 */
export const mysteryRowsForReel = (round, reelIndex) => {
  const feature = readMysteryFeature(round);
  if (!feature) return [];
  return feature.coords
    .filter(([reel]) => reel === reelIndex)
    .map(([, row]) => row);
};

/**
 * One reel's symbols with every mystery cell showing what it BECAME.
 *
 * The twin of maskReelForMystery, for a board that is already finished — a
 * round played before the feature that is now being restored behind it. It
 * writes the replacement into the mystery cells rather than trusting the
 * column, so it is right whichever form the restored screen arrived in.
 *
 * Returns the SAME array when there is nothing to resolve.
 *
 * @param {number[]} reelData
 * @param {object}   round
 * @param {number}   reelIndex
 * @returns {number[]}
 */
export const resolveReelForMystery = (reelData, round, reelIndex) => {
  if (!Array.isArray(reelData)) return reelData;
  const feature = readMysteryFeature(round);
  const rows = mysteryRowsForReel(round, reelIndex);
  if (!feature || !rows.length) return reelData;

  // ── A GOLDEN mystery is not resolved: its cells keep the mystery id ──
  //
  // Its replacement is 10, the `bonus` symbol, whose texture is `myg` — the
  // CLOSED golden doors. A golden cell never becomes that: it "keeps this
  // symbol id for its whole life" (SymbolData, the mystery's entry), and what
  // it shows is the composite painted over it. Resolved, every golden cell took
  // the doors' identity, and the one the composites skip — the gamble scatter,
  // painted by its own block — came back as golden doors in the room.
  // Vladimir, 2026-09-24: *"we ended up with golden doors instead of a symbol
  // we won."* So a golden round is masked, exactly as it was before this
  // function existed.
  if (feature.replacementId === BONUS_SYMBOL_ID) {
    return maskReelForMystery(reelData, round, reelIndex);
  }

  const resolved = [...reelData];
  for (const row of rows) {
    if (row >= 0 && row < resolved.length) resolved[row] = feature.replacementId;
  }
  return resolved;
};

/**
 * One reel's symbols, with its mystery cells masked back to the mystery
 * symbol so they land, bounce and idle as mystery.
 *
 * Returns the SAME array when there is nothing to mask, so the common case
 * allocates nothing and reference equality still holds for callers that
 * compare.
 *
 * @param {number[]}  reelData  symbols for this reel, from the resolved board
 * @param {object}    round
 * @param {number}    reelIndex
 * @returns {number[]}
 */
export const maskReelForMystery = (reelData, round, reelIndex) => {
  if (!Array.isArray(reelData)) return reelData;

  const rows = mysteryRowsForReel(round, reelIndex);
  if (!rows.length) return reelData;

  const masked = [...reelData];
  for (const row of rows) {
    if (row >= 0 && row < masked.length) masked[row] = MYSTERY_SYMBOL_ID;
  }
  return masked;
};

/**
 * Every cell to reveal, grouped by reel, in the shape the orchestrator wants.
 *
 * @param {object} round
 * @returns {Array<{reelIndex: number, rows: number[], replacementId: number}>}
 */
export const mysteryTargetsByReel = (round) => {
  const feature = readMysteryFeature(round);
  if (!feature) return [];

  const byReel = new Map();
  for (const [reel, row] of feature.coords) {
    if (!byReel.has(reel)) byReel.set(reel, []);
    byReel.get(reel).push(row);
  }

  return [...byReel.entries()].map(([reelIndex, rows]) => ({
    reelIndex,
    rows,
    replacementId: feature.replacementId,
  }));
};

export default maskReelForMystery;
